import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { buildFxModel } from './fxModels';
import { mergeModelPrimitives } from './mergedMaterial';
import { isProceduralModel, resolveModelId } from './modelMap';

export interface ManifestEntry {
  category: string;
  file: string;
  tris?: number;
  clips?: string[];
  radius?: number;
  height?: number;
  collider?: { type: 'cylinder'; radius: number } | { type: 'box'; size: [number, number, number] };
  /** `weapon`/`offhand` bones, or a prop's `socket_*` nodes. */
  sockets?: string[];
  /** Mesh node names the game hides when a boss part breaks. */
  breakable?: string[];
  /** Gatherable node type. */
  node?: string;
  walkable?: boolean;
  /** Gear modelled in hand (socket) space. */
  socket_space?: boolean;
  bytes?: number;
}

export interface Manifest {
  version: number;
  assets: Record<string, ManifestEntry>;
}

/** A prop's light or fire socket (`socket_light` / `socket_fire` node), in model space. */
export interface PropSocket {
  kind: 'fire' | 'light';
  pos: THREE.Vector3;
  color: THREE.Color;
  /** Authoring intensity (Blender watts-ish); the renderer maps it to its own scale. */
  intensity: number;
  distance: number;
}

export interface ModelTemplate {
  id: string;
  scene: THREE.Object3D;
  clips: THREE.AnimationClip[];
  skinned: boolean;
  entry: ManifestEntry | null;
  /** True when the model is a procedural stand-in (asset missing). */
  placeholder: boolean;
  /** Built in code (`fx_*` helpers); has no clips and animates procedurally. */
  procedural: boolean;
  sockets: PropSocket[];
}

const BASE = `${import.meta.env.BASE_URL}assets/models/`;

/**
 * Loads and caches glTF models listed in the asset manifest. Instances are
 * produced with SkeletonUtils.clone so skinned meshes get independent
 * skeletons. Ids resolve through `modelMap` (aliases and procedural `fx_*`
 * models); anything still missing falls back to a simple stand-in so the game
 * never breaks on a missing file.
 */
export class AssetLibrary {
  manifest: Manifest = { version: 0, assets: {} };
  private loader = new GLTFLoader();
  private cache = new Map<string, Promise<ModelTemplate>>();

  constructor() {
    this.loader.setMeshoptDecoder(MeshoptDecoder);
  }

  async init(): Promise<void> {
    try {
      const res = await fetch(`${BASE}manifest.json`, { cache: 'no-cache' });
      if (res.ok) this.manifest = (await res.json()) as Manifest;
    } catch (e) {
      console.warn('Asset manifest unavailable, using placeholders', e);
    }
  }

  has(id: string): boolean {
    return id in this.manifest.assets;
  }

  entry(id: string): ManifestEntry | null {
    return this.manifest.assets[id] ?? null;
  }

  /** Resolves an asset id through the alias chain (procedural ids resolve to themselves); null when nothing matches. */
  resolve(id: string): string | null {
    return resolveModelId(id, (x) => this.has(x));
  }

  load(id: string): Promise<ModelTemplate> {
    let p = this.cache.get(id);
    if (!p) {
      p = this.fetchModel(id);
      this.cache.set(id, p);
    }
    return p;
  }

  /** Preloads a list of models in parallel. */
  async preload(ids: Iterable<string>): Promise<void> {
    await Promise.all([...new Set(ids)].map((id) => this.load(id)));
  }

  private async fetchModel(requested: string): Promise<ModelTemplate> {
    const id = this.resolve(requested) ?? requested;
    if (isProceduralModel(id)) {
      const fx = buildFxModel(id);
      if (fx) {
        const entry: ManifestEntry = { category: 'fx', file: '', radius: fx.radius, height: fx.height };
        return { id, scene: fx.scene, clips: [], skinned: false, entry, placeholder: false, procedural: true, sockets: [] };
      }
    }
    const entry = this.entry(id);
    if (!entry) return placeholderTemplate(id);
    try {
      const gltf: GLTF = await this.loader.loadAsync(BASE + entry.file);
      let skinned = false;
      // One draw call per mesh instead of one per material (see mergedMaterial.ts).
      mergeModelPrimitives(gltf.scene);
      gltf.scene.updateMatrixWorld(true);
      const sockets: PropSocket[] = [];
      gltf.scene.traverse((o) => {
        if ((o as THREE.SkinnedMesh).isSkinnedMesh) skinned = true;
        if ((o as THREE.Mesh).isMesh) {
          const m = o as THREE.Mesh;
          m.castShadow = true;
          m.receiveShadow = true;
          tuneMaterial(m.material);
        }
        const sock = socketOf(o);
        if (sock) sockets.push(sock);
      });
      return { id, scene: gltf.scene, clips: gltf.animations, skinned, entry, placeholder: false, procedural: false, sockets };
    } catch (e) {
      console.warn(`Failed to load model '${id}', using placeholder`, e);
      return placeholderTemplate(id);
    }
  }

  /** Independent copy of a model (skeleton-safe). */
  instantiate(t: ModelTemplate): THREE.Object3D {
    return t.skinned ? SkeletonUtils.clone(t.scene) : t.scene.clone(true);
  }
}

/** `socket_fire` / `socket_light` nodes carry `{color, intensity, distance}` extras. */
function socketOf(o: THREE.Object3D): PropSocket | null {
  const kind = o.name.startsWith('socket_fire') ? 'fire' : o.name.startsWith('socket_light') ? 'light' : null;
  if (!kind) return null;
  const x = o.userData as { color?: string; intensity?: number; distance?: number };
  return {
    kind,
    pos: new THREE.Vector3().setFromMatrixPosition(o.matrixWorld),
    color: new THREE.Color(typeof x.color === 'string' ? x.color : kind === 'fire' ? '#ff8a3a' : '#ffb347'),
    intensity: typeof x.intensity === 'number' ? x.intensity : 4,
    distance: typeof x.distance === 'number' ? x.distance : 7,
  };
}

function tuneMaterial(mat: THREE.Material | THREE.Material[]): void {
  const list = Array.isArray(mat) ? mat : [mat];
  for (const m of list) {
    if (m.userData.merged) continue;
    if (m instanceof THREE.MeshStandardMaterial) {
      // Vertex-coloured stylised assets: metals take the image-based light
      // (their shine), rough surfaces keep a little so shadows are not flat.
      m.envMapIntensity = m.metalness > 0.4 ? 1.0 : 0.55;
      const glowing = m.emissive.getHex() !== 0 && m.emissiveIntensity > 0;
      m.userData.glowMat = glowing;
      if (glowing) m.emissiveIntensity = Math.max(m.emissiveIntensity, 2.5);
    }
  }
}

/** Procedural stand-in: capsule-ish figure for creatures, box for props. */
function placeholderTemplate(id: string): ModelTemplate {
  const g = new THREE.Group();
  const isProp = !/^(hero|mob|boss|npc)_/.test(id);
  const color = new THREE.Color().setHSL((hashId(id) % 360) / 360, 0.35, 0.5);
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.8 });
  if (isProp) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), mat);
    m.position.y = 0.5;
    m.castShadow = true;
    g.add(m);
  } else {
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.35, 1.0, 4, 10), mat);
    body.position.y = 0.9;
    body.castShadow = true;
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.3, 8), mat);
    nose.rotation.x = Math.PI / 2;
    nose.position.set(0, 1.4, 0.35);
    g.add(body, nose);
  }
  return { id, scene: g, clips: [], skinned: false, entry: null, placeholder: true, procedural: false, sockets: [] };
}

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}
